import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList, MessageCircle, Camera as CameraIcon, DollarSign, CheckCircle2, XCircle, Clock } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";

const statusColors = {
  pending: "bg-amber-500/15 text-amber-600 border-0",
  replied: "bg-blue-500/15 text-blue-600 border-0",
  accepted: "bg-emerald-500/15 text-emerald-600 border-0",
  declined: "bg-red-500/15 text-red-600 border-0",
};

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [openQuote, setOpenQuote] = useState(null);
  const tab = params.get("tab") || "quotes";

  const load = async () => {
    try {
      const { data } = await api.get("/quotes/mine");
      setQuotes(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const qid = params.get("quote");
    if (qid) {
      const q = quotes.find(x => x.quote_id === qid);
      if (q) setOpenQuote(q);
    }
  }, [params, quotes]);

  const setStatus = async (quote_id, status) => {
    try {
      await api.post(`/quotes/${quote_id}/status`, { status });
      toast.success(status === "accepted" ? "Quote accepted — we'll be in touch to book install!" : "Quote declined.");
      setOpenQuote(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-12">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-eagle-gold font-semibold mb-2">Welcome back</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">{user.name}</h1>
      </div>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList data-testid="dashboard-tabs">
          <TabsTrigger value="quotes" data-testid="tab-quotes"><ClipboardList className="h-4 w-4 mr-2" />My Quotes</TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat"><MessageCircle className="h-4 w-4 mr-2" />Messages</TabsTrigger>
        </TabsList>

        <TabsContent value="quotes" className="mt-6">
          {quotes.length === 0 ? (
            <Card className="p-16 text-center">
              <ClipboardList className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <div className="font-display text-xl mb-2">No quotes yet</div>
              <p className="text-sm text-muted-foreground mb-6">Get a free camera plan for your property.</p>
              <Link to="/quote"><Button className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover rounded-full" data-testid="dashboard-cta-quote">Request a Quote</Button></Link>
            </Card>
          ) : (
            <div className="space-y-3">
              {quotes.map(q => (
                <Card key={q.quote_id} className="p-5 flex flex-col md:flex-row md:items-center gap-4 hover:border-eagle-gold transition-colors cursor-pointer" onClick={() => setOpenQuote(q)} data-testid={`quote-row-${q.quote_id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-display font-semibold text-lg">{q.address}</div>
                      <Badge className={statusColors[q.status] || ""}>{q.status}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {q.num_cameras} cameras · {q.property_type} · {q.installation_type}
                    </div>
                  </div>
                  <div className="text-right">
                    {q.price ? (
                      <div className="font-display text-2xl font-bold text-eagle-gold">${q.price.toLocaleString()}</div>
                    ) : (
                      <div className="text-sm text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> Awaiting quote</div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">{new Date(q.created_at).toLocaleDateString()}</div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="chat" className="mt-6">
          <Card className="p-8">
            <MessageCircle className="h-10 w-10 text-eagle-gold mb-3" />
            <div className="font-display text-xl mb-2">Message us anytime</div>
            <p className="text-sm text-muted-foreground mb-4">Use the chat widget at the bottom-right corner of any page. We&apos;ll email you when we reply.</p>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!openQuote} onOpenChange={(o) => !o && setOpenQuote(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {openQuote && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Quote for {openQuote.address}</DialogTitle>
              </DialogHeader>
              <div className="space-y-5">
                <div className="flex flex-wrap gap-3 text-sm">
                  <Badge className={statusColors[openQuote.status] || ""}>{openQuote.status}</Badge>
                  <span className="text-muted-foreground">{openQuote.num_cameras} cameras · {openQuote.installation_type}</span>
                </div>

                {openQuote.price ? (
                  <div className="p-5 rounded-xl bg-eagle-gold/10 border border-eagle-gold/30">
                    <div className="text-xs uppercase tracking-widest text-eagle-gold font-semibold">Your quote</div>
                    <div className="font-display text-4xl font-bold mt-1">${openQuote.price.toLocaleString()} <span className="text-sm text-muted-foreground font-normal">AUD</span></div>
                    {openQuote.breakdown && <div className="mt-3 text-sm whitespace-pre-wrap">{openQuote.breakdown}</div>}
                  </div>
                ) : (
                  <div className="p-5 rounded-xl border border-dashed border-border text-sm text-muted-foreground flex items-center gap-2">
                    <Clock className="h-4 w-4" /> We&apos;re reviewing your request. You&apos;ll get an email within 24 hours.
                  </div>
                )}

                {openQuote.marked_images?.length > 0 && (
                  <div>
                    <div className="font-display font-semibold mb-3 flex items-center gap-2">
                      <CameraIcon className="h-4 w-4 text-eagle-gold" /> Camera Placement Plan
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {openQuote.marked_images.map((src, i) => (
                        <img key={i} src={src} alt="" className="w-full rounded-lg border border-border" />
                      ))}
                    </div>
                  </div>
                )}

                {openQuote.images?.length > 0 && (
                  <div>
                    <div className="font-display font-semibold mb-3">Photos you submitted</div>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {openQuote.images.map((src, i) => <img key={i} src={src} alt="" className="aspect-square object-cover rounded-lg border border-border" />)}
                    </div>
                  </div>
                )}

                {openQuote.status === "replied" && (
                  <div className="flex gap-3 pt-2">
                    <Button onClick={() => setStatus(openQuote.quote_id, "accepted")} className="flex-1 bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover rounded-full h-11" data-testid="quote-accept-btn">
                      <CheckCircle2 className="h-4 w-4 mr-2" /> Accept Quote
                    </Button>
                    <Button onClick={() => setStatus(openQuote.quote_id, "declined")} variant="outline" className="rounded-full h-11" data-testid="quote-decline-btn">
                      <XCircle className="h-4 w-4 mr-2" /> Decline
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}